import React, { useState, useEffect } from "react";
import { submitFlightResponse, getAnsweredRequest } from "apis/requestServices";
import { ModalProps } from "./Types";
import { useToastStore } from "store/toastStore";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import DefaultAirLogo from "../../assets/images/logo.svg";
import FlightRouteArc from "./FlightRouteArc";

const RequestReplyTab: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  logoImg,
  airlineName,
  flightNumber,
  flightDate,
  airlineId,
  departureAirport,
  arrivalAirport,
  departureTime,
  arrivalTime,
  flightDuration,
  requestDetailId,
  departureCity,
  arrivalCity,
}) => {
  const defaultSeatData = {
    requestDetailId: 0,
    firstClass: 0,
    businessClass: 0,
    economyPlusClass: 0,
    economyClass: 0,
    nonRevListed: 0,
    nonFirst: 0,
    nonBusiness: 0,
    nonEconomyPlus: 0,
    nonEconomy: 0,
    moreFirstClass: false,
    moreBusinessClass: false,
    moreEcoClass: false,
    moreEcoPlus: false,
    comments: "",
  };

  const [seatData, setSeatData] = useState(defaultSeatData);
  const [error, setError] = useState<string | null>(null);
  const [focusedClass, setFocusedClass] = useState<
    "First" | "Economy" | "Eco+" | "Business" | "NonRev"
  >("First");
  const [noClassChecked, setNoClassChecked] = useState({
    First: false,
    Business: false,
    Economy: false,
    "Eco+": false,
  });
  const [unknownNonRevCount, setUnknownNonRevCount] = useState(false);
  const [nonRevValidationError, setNonRevValidationError] = useState<
    string | null
  >(null);
  const [dontKnowNonRev, setDontKnowNonRev] = useState(false);

  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSeatData({
        ...defaultSeatData,
        requestDetailId: requestDetailId || 0,
      });
      setError(null);
      setNonRevValidationError(null);
      setFocusedClass("First");
      setNoClassChecked({
        First: false,
        Business: false,
        Economy: false,
        "Eco+": false,
      });
      setUnknownNonRevCount(false);
    }
  }, [isOpen, requestDetailId]);
  const CLASS_SEAT_FIELDS = [
    "firstClass",
    "businessClass",
    "economyPlusClass",
    "economyClass",
  ];

  const handleInputChange = (
    field: string,
    value: number | boolean | string
  ) => {
    // Clamp the top-4 class inputs to [-100, 100] — values outside that range
    // aren't valid seat counts.
    if (CLASS_SEAT_FIELDS.includes(field) && typeof value === "number") {
      value = Math.max(-100, Math.min(100, value));
    }

    // Clear non-rev validation error if any of these fields are changed
    if (
      field === "nonRevListed" ||
      field === "nonFirst" ||
      field === "nonBusiness" ||
      field === "nonEconomyPlus" ||
      field === "nonEconomy"
    ) {
      setNonRevValidationError(null);
    }

    // Handle "NoClass" checkbox logic
    if (field === "NoClass") {
      const seatField =
        focusedClass === "First"
          ? "firstClass"
          : focusedClass === "Business"
            ? "businessClass"
            : focusedClass === "Eco+"
              ? "economyPlusClass"
              : "economyClass";
      const moreField =
        focusedClass === "First"
          ? "moreFirstClass"
          : focusedClass === "Business"
            ? "moreBusinessClass"
            : focusedClass === "Eco+"
              ? "moreEcoPlus"
              : "moreEcoClass";

      let nonRevField = "";
      if (focusedClass === "First") nonRevField = "nonFirst";
      else if (focusedClass === "Business") nonRevField = "nonBusiness";
      else if (focusedClass === "Eco+") nonRevField = "nonEconomyPlus";
      else if (focusedClass === "Economy") nonRevField = "nonEconomy";

      setNoClassChecked((prev) => ({
        ...prev,
        [focusedClass]: true,
      }));

      setSeatData((prev) => {
        const updated = {
          ...prev,
          [seatField]: 0,
          [moreField]: false,
          ...(nonRevField && { [nonRevField]: 0 }),
        };

        const hasAnyNonRevClassValue =
          updated.nonFirst > 0 ||
          updated.nonBusiness > 0 ||
          updated.nonEconomyPlus > 0 ||
          updated.nonEconomy > 0;

        if (!unknownNonRevCount && hasAnyNonRevClassValue) {
          updated.nonRevListed =
            Number(updated.nonFirst || 0) +
            Number(updated.nonBusiness || 0) +
            Number(updated.nonEconomyPlus || 0) +
            Number(updated.nonEconomy || 0);
        }

        return updated;
      });
    }
    // Handle unknownNonRevCount toggle
    else if (field === "unknownNonRevCount") {
      const isChecked = value as boolean;
      setNonRevValidationError(null);
      setUnknownNonRevCount(isChecked);
      if (isChecked) {
        setSeatData((prev) => ({
          ...prev,
          nonFirst: 0,
          nonBusiness: 0,
          nonEconomyPlus: 0,
          nonEconomy: 0,
          nonRevListed: 0,
        }));
      }
    }
    // Handle changes to per-class or non-rev fields
    else {
      if (
        (field === "firstClass" && focusedClass === "First") ||
        (field === "businessClass" && focusedClass === "Business") ||
        (field === "economyPlusClass" && focusedClass === "Eco+") ||
        (field === "economyClass" && focusedClass === "Economy")
      ) {
        if (value !== null) {
          setNoClassChecked((prev) => ({
            ...prev,
            [focusedClass]: false,
          }));
        }
        if (value === "" || value === 0) {
          const nonRevField =
            field === "firstClass"
              ? "nonFirst"
              : field === "businessClass"
                ? "nonBusiness"
                : field === "economyPlusClass"
                  ? "nonEconomyPlus"
                  : "nonEconomy";
          setSeatData((prev) => ({
            ...prev,
            [field]: value,
            [nonRevField]: 0,
          }));
          return;
        }
      }

      // Auto-calculate nonRevListed if any per-class non-rev field changes
      if (
        field === "nonFirst" ||
        field === "nonBusiness" ||
        field === "nonEconomyPlus" ||
        field === "nonEconomy"
      ) {
        const updatedSeatData = {
          ...seatData,
          [field]: Number(value),
        };

        const hasAnyNonRevClassValue =
          updatedSeatData.nonFirst > 0 ||
          updatedSeatData.nonBusiness > 0 ||
          updatedSeatData.nonEconomyPlus > 0 ||
          updatedSeatData.nonEconomy > 0;

        if (!unknownNonRevCount && hasAnyNonRevClassValue) {
          const total =
            Number(updatedSeatData.nonFirst || 0) +
            Number(updatedSeatData.nonBusiness || 0) +
            Number(updatedSeatData.nonEconomyPlus || 0) +
            Number(updatedSeatData.nonEconomy || 0);

          updatedSeatData.nonRevListed = total;
        }

        setSeatData(updatedSeatData);
        return;
      }

      // Default behavior for all other fields
      setSeatData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleClose = () => {
    setSeatData(defaultSeatData);
    setError(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    // Reset errors
    setError(null);
    setNonRevValidationError(null);

    // Only validate Non-Rev Listed if toggle is OFF
    if (!unknownNonRevCount) {
      const {
        nonRevListed,
        nonFirst,
        nonBusiness,
        nonEconomyPlus,
        nonEconomy,
      } = seatData;

      const hasNonRevFields =
        (nonFirst || 0) > 0 ||
        (nonBusiness || 0) > 0 ||
        (nonEconomyPlus || 0) > 0 ||
        (nonEconomy || 0) > 0;

      const totalNonRevPerClass =
        (nonFirst || 0) +
        (nonBusiness || 0) +
        (nonEconomyPlus || 0) +
        (nonEconomy || 0);

      // Case 1: Any per-class field is filled — enforce match with nonRevListed
      if (hasNonRevFields) {
        if (!nonRevListed || nonRevListed < 0) {
          setNonRevValidationError(
            "Please provide the number of listed Non-Rev passengers."
          );
          return;
        }

        if (totalNonRevPerClass !== nonRevListed) {
          setNonRevValidationError(
            "Total of 'Listed Non-Rev Pax Per Class' must match the 'Non Rev' value."
          );
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      // Race guard: another user may have answered this load while the form
      // was open. Recheck right before posting to avoid a duplicate answer.
      try {
        await getAnsweredRequest(Number(requestDetailId));
        useToastStore
          .getState()
          .showToast(
            "This request has already been answered by someone else.",
            "info",
            3000
          );
        setSubmitting(false);
        handleClose();
        return;
      } catch (checkError) {
        // No existing answer found — safe to proceed with submit.
      }

      await submitFlightResponse({ ...seatData, requestDetailId, comments: comment });
      setComment("");
      setSeatData(defaultSeatData);
      useToastStore
        .getState()
        .showToast("Request answered successfully!", "success", 3000);
      // Parent removes the row, refreshes the token balance, and closes the
      // drawer (releasing the lock in the background).
      onSubmit?.();
    } catch (error: any) {
      useToastStore
        .getState()
        .showToast(
          error?.response?.data?.message ||
            "Failed to submit the response. Please try again.",
          "error",
          3000
        );
    } finally {
      setSubmitting(false);
    }
  };

  const getInputValue = (
    classType: "First" | "Economy" | "Eco+" | "Business"
  ) => {
    const isNoClass = noClassChecked[classType];
    const value =
      classType === "First"
        ? seatData.firstClass
        : classType === "Business"
          ? seatData.businessClass
          : classType === "Eco+"
            ? seatData.economyPlusClass
            : seatData.economyClass;
    if (isNoClass) return "";
    return value === 0 ? "" : value;
  };

  const showToggleOptions = focusedClass !== "NonRev";

  if (!isOpen) return null;

  const classInputStyle = (
    value: number,
    classType: "First" | "Economy" | "Eco+" | "Business"
  ) => {
    const isNoClass = noClassChecked[classType];
    return `w-full text-center h-9 border rounded-lg text-sm font-semibold placeholder:text-sm placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[#FBBF24] transition-all ${
      value < 0
        ? "border-red-400 bg-red-50 text-red-600"
        : "border-gray-200 text-gray-800"
    } ${isNoClass ? "opacity-40 bg-gray-100" : "bg-white"}`;
  };

  const nonRevClassInputStyle = (isEnabled: boolean) =>
    `w-full text-center h-9 border rounded-lg text-sm font-semibold placeholder:text-sm placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[#FBBF24] transition-all ${
      isEnabled && !unknownNonRevCount
        ? "border-gray-200 text-gray-800 bg-white"
        : "border-gray-100 bg-gray-50 text-gray-300"
    }`;

  const toggleStyle =
    "relative w-10 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FBBF24] flex-shrink-0";

  return (
    <div
      id="default-modal"
      tabIndex={-1}
      aria-hidden={!isOpen}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3"
    >
      <div className="relative w-full max-w-[440px] max-h-[calc(100dvh-24px)]">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-24px)]">
          {/* Header */}
          <div className="flex items-center justify-between bg-[#192227] px-5 py-2.5 shrink-0">
            <h2 className="text-base font-semibold text-white">Submit Open Request</h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-5 pb-3 pt-2.5 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {/* Flight Route Card */}
            <div className="bg-gray-50 rounded-xl px-4 py-2 mb-2">
              <div className="flex items-center justify-between">
                {/* Departure */}
                <div className="text-left">
                  <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                    <i className="st-arrow-slant text-gray-400 text-[10px]"></i>
                    <span>{departureTime}</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{departureAirport}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{departureCity || " "}</p>
                </div>

                {/* Route Line */}
                <FlightRouteArc duration={flightDuration} className="flex-1 mx-3" />

                {/* Arrival */}
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1 text-xs text-gray-400 mb-1">
                    <i className="st-arrow-slant text-gray-400 text-[10px] rotate-90"></i>
                    <span>{arrivalTime}</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{arrivalAirport}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{arrivalCity || " "}</p>
                </div>
              </div>
            </div>

            {/* Date / Flight Name / Flight Number */}
            <div className="grid grid-cols-3 gap-3 px-1 mb-2 items-center">
            {/* Date */}
            <div className="min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                Date
              </p>
              <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">
                {flightDate}
              </p>
            </div>

            {/* Flight Name */}
            <div className="min-w-0 text-center">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide text-center">
                Flight name
              </p>

              <div className="flex items-center justify-center gap-2 mt-0.5 h-5">
                <img
                  className="w-5 h-5 object-contain rounded shrink-0"
                  src={logoImg || DefaultAirLogo}
                  alt={airlineName || "Airline"}
                  onError={(e) => {
                    e.currentTarget.src = "/assets/logos/air-logo.png";
                  }}
                />

                <p className="text-sm font-medium text-gray-800 truncate">
                  {airlineName}
                </p>
              </div>
            </div>

            {/* Flight Number */}
            <div className="text-right min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                Flight no
              </p>
              <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">
                {flightNumber}
              </p>
            </div>
          </div>
            {/* Class Inputs */}
            <div className="grid grid-cols-4 gap-2 mb-2">
              <input
                type="number"
                min={-100}
                max={100}
                placeholder="First"
                onFocus={() => setFocusedClass("First")}
                value={getInputValue("First")}
                onChange={(e) =>
                  handleInputChange(
                    "firstClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.firstClass, "First")}
              />
              <input
                type="number"
                min={-100}
                max={100}
                placeholder="Eco"
                onFocus={() => setFocusedClass("Economy")}
                value={getInputValue("Economy")}
                onChange={(e) =>
                  handleInputChange(
                    "economyClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.economyClass, "Economy")}
              />
              <input
                type="number"
                min={-100}
                max={100}
                placeholder="Eco+"
                onFocus={() => setFocusedClass("Eco+")}
                value={getInputValue("Eco+")}
                onChange={(e) =>
                  handleInputChange(
                    "economyPlusClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.economyPlusClass, "Eco+")}
              />
              <input
                type="number"
                min={-100}
                max={100}
                placeholder="Bus"
                onFocus={() => setFocusedClass("Business")}
                value={getInputValue("Business")}
                onChange={(e) =>
                  handleInputChange(
                    "businessClass",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className={classInputStyle(seatData.businessClass, "Business")}
              />
            </div>
            {error && <p className="text-red-500 text-xs mb-2 text-center">{error}</p>}

            {/* Listed Passengers */}
            <div className="flex flex-col items-center mb-2">
              <p className="text-sm font-medium text-gray-700 mb-1.5">Listed Passengers</p>
              <input
                type="number"
                min="0"
                placeholder="Non Rev"
                readOnly
                title="Auto-calculated from 'Listed Non-Rev Pax Per Class' below"
                onFocus={() => setFocusedClass("NonRev")}
                value={seatData.nonRevListed || ""}
                className={`w-24 text-center h-9 border rounded-lg text-sm font-semibold placeholder:text-sm placeholder:font-normal cursor-not-allowed ${
                  unknownNonRevCount
                    ? "bg-gray-50 border-gray-100 text-gray-300"
                    : "border-gray-200 text-gray-800 bg-gray-50"
                }`}
                disabled={dontKnowNonRev}
              />
              {nonRevValidationError && (
                <div className="mt-2 flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <ExclamationCircleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                  <p className="text-red-600 text-xs">{nonRevValidationError}</p>
                </div>
              )}
            </div>

            {/* Listed Non-Rev Pax Per Class */}
            <div className="border border-dashed border-gray-300 rounded-xl px-4 py-2 mb-2">
                <p className="text-sm font-medium text-gray-700 mb-1.5 text-center">
                  Listed Non-Rev Pax Per Class
                </p>
                <div className="grid grid-cols-4 gap-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="First"
                    value={seatData.nonFirst === 0 ? "" : seatData.nonFirst}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^\d*$/.test(inputValue)) {
                        const numValue = parseInt(inputValue, 10);
                        handleInputChange(
                          "nonFirst",
                          inputValue === "" ? "" : Math.min(100, Math.max(0, numValue))
                        );
                      }
                    }}
                    className={nonRevClassInputStyle(seatData.firstClass !== 0)}
                    disabled={!seatData.firstClass || unknownNonRevCount}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Eco"
                    value={seatData.nonEconomy === 0 ? "" : seatData.nonEconomy}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^\d*$/.test(inputValue)) {
                        const numValue = parseInt(inputValue, 10);
                        handleInputChange(
                          "nonEconomy",
                          inputValue === "" ? "" : Math.min(100, Math.max(0, numValue))
                        );
                      }
                    }}
                    className={nonRevClassInputStyle(seatData.economyClass !== 0)}
                    disabled={!seatData.economyClass || unknownNonRevCount}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Eco+"
                    value={seatData.nonEconomyPlus === 0 ? "" : seatData.nonEconomyPlus}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^\d*$/.test(inputValue)) {
                        const numValue = parseInt(inputValue, 10);
                        handleInputChange(
                          "nonEconomyPlus",
                          inputValue === "" ? "" : Math.min(100, Math.max(0, numValue))
                        );
                      }
                    }}
                    className={nonRevClassInputStyle(seatData.economyPlusClass !== 0)}
                    disabled={!seatData.economyPlusClass || unknownNonRevCount}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Bus"
                    value={seatData.nonBusiness === 0 ? "" : seatData.nonBusiness}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^\d*$/.test(inputValue)) {
                        const numValue = parseInt(inputValue, 10);
                        handleInputChange(
                          "nonBusiness",
                          inputValue === "" ? "" : Math.min(100, Math.max(0, numValue))
                        );
                      }
                    }}
                    className={nonRevClassInputStyle(seatData.businessClass !== 0)}
                    disabled={!seatData.businessClass || unknownNonRevCount}
                  />
                </div>
            </div>

            {/* Toggle Options */}
            <div className="space-y-0 mb-2">
              {/* Don't know non-rev count */}
              <label className="flex items-start gap-3 py-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={unknownNonRevCount}
                  onChange={(e) =>
                    handleInputChange("unknownNonRevCount", e.target.checked)
                  }
                  onClick={() => {
                    setDontKnowNonRev((prev) => !prev);
                  }}
                />
                <div className={`${toggleStyle} mt-0.5`}></div>
                <span className="text-sm text-gray-500">
                  I don't know how many non-rev passengers are listed
                </span>
              </label>

              {showToggleOptions && (
                <>
                  {/* Overbooked */}
                  <label className="flex items-start gap-3 py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={
                        (focusedClass === "First"
                          ? seatData.firstClass
                          : focusedClass === "Business"
                            ? seatData.businessClass
                            : focusedClass === "Eco+"
                              ? seatData.economyPlusClass
                              : seatData.economyClass) < 0
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const seatField =
                          focusedClass === "First"
                            ? "firstClass"
                            : focusedClass === "Business"
                              ? "businessClass"
                              : focusedClass === "Eco+"
                                ? "economyPlusClass"
                                : "economyClass";
                        handleInputChange(seatField, isChecked ? -1 : 0);
                      }}
                      disabled={
                        focusedClass === "First"
                          ? seatData.firstClass === null
                          : focusedClass === "Business"
                            ? seatData.businessClass === null
                            : focusedClass === "Eco+"
                              ? seatData.economyPlusClass === null
                              : seatData.economyClass === null
                      }
                    />
                    <div className={`${toggleStyle} mt-0.5`}></div>
                    <span className="text-sm text-gray-500">Overbooked</span>
                  </label>

                  {/* No class */}
                  <label className="flex items-start gap-3 py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      id="no-class-checkbox"
                      checked={noClassChecked[focusedClass]}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setNoClassChecked((prev) => ({
                          ...prev,
                          [focusedClass]: isChecked,
                        }));
                        if (isChecked) {
                          handleInputChange("NoClass", true);
                        } else {
                          const seatField =
                            focusedClass === "First"
                              ? "firstClass"
                              : focusedClass === "Business"
                                ? "businessClass"
                                : focusedClass === "Eco+"
                                  ? "economyPlusClass"
                                  : "economyClass";
                          handleInputChange(seatField, "");
                        }
                      }}
                    />
                    <div className={`${toggleStyle} mt-0.5`}></div>
                    <span className="text-sm text-gray-500">
                      This flight has no {focusedClass} class
                    </span>
                  </label>

                  {/* More seats available */}
                  <label
                    className={`flex items-start gap-3 py-0.5 ${noClassChecked[focusedClass] ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={
                        focusedClass === "First"
                          ? seatData.moreFirstClass
                          : focusedClass === "Business"
                            ? seatData.moreBusinessClass
                            : focusedClass === "Eco+"
                              ? seatData.moreEcoPlus
                              : seatData.moreEcoClass
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const field =
                          focusedClass === "First"
                            ? "moreFirstClass"
                            : focusedClass === "Business"
                              ? "moreBusinessClass"
                              : focusedClass === "Eco+"
                                ? "moreEcoPlus"
                                : "moreEcoClass";
                        handleInputChange(field, isChecked);
                      }}
                      disabled={
                        noClassChecked[focusedClass] ||
                        (focusedClass === "First"
                          ? seatData.firstClass === null
                          : focusedClass === "Business"
                            ? seatData.businessClass === null
                            : focusedClass === "Eco+"
                              ? seatData.economyPlusClass === null
                              : seatData.economyClass === null)
                      }
                    />
                    <div className={`${toggleStyle} mt-0.5`}></div>
                    <span className="text-sm text-gray-500">
                      There are more seats available in {focusedClass} class but I don't know how many
                    </span>
                  </label>
                </>
              )}
            </div>

            {/* Comment */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comment (optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment if needed"
                rows={1}
                className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FBBF24] focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-1">
              <button
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  (!unknownNonRevCount &&
                    (!seatData.nonRevListed || seatData.nonRevListed < 0))
                }
                className={`w-full max-w-[210px] py-2.5 bg-[#FBBF24] hover:bg-[#F59E0B] text-[#192227] font-semibold text-[15px] rounded-full shadow-md transition-all ${
                  submitting ||
                  (!unknownNonRevCount &&
                    (!seatData.nonRevListed || seatData.nonRevListed < 0))
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestReplyTab;
